'use client'

import React, { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react'
import toast from 'react-hot-toast'
import { api } from '../configs/api'
import { useAuth } from './AuthContext'
import { FiltersType, FilterType } from './CommonContext'
import { businessFilter } from '../functions/businessFilter'
import { buildBusinessSearchIndex, type BusinessSearchIndex } from '../functions/businessSearchIndex'
import { useProduct } from './ProductContext'

type ModeType = 'pipeline' | 'list'

//* Tipagem dos dados de business
export type PipelineType = {
    id: string
    value: string
    label: string
    steps: PipelineStepType[]
}

export type PipelineStepType = {
    id: string
    title: string
    color?: string
    business: PipelineStepBusinessType[]
}

//* Dados pesquisáveis extras que o backend envia junto ao negócio (empresa, contatos, descrição e notas)
export type BusinessSearchDataType = {
    company?: { id: number; name: string } | null
    contacts?: { id: number; name: string; email?: string | null; phone?: string | null }[]
    description?: string
    noteDescriptions?: string[]
}

export type PipelineStepBusinessType = BusinessSearchDataType & {
    id: string
    title: string
    subtitle?: string
    value?: string
    products?: {
        id: string
        name: string
    }[]
    targets?: FilterType[]
    stepID: string
    dateNotify: string | null
    isExpired: boolean
    professionalID: number | null
}

export type BusinessDataType = {
    pipelines: PipelineType[]
}

type BusinessType = {
    loading: boolean
    startLoading: () => void
    stopLoading: () => void
    filteredBusiness: BusinessDataType
    setFilteredBusiness: (value: BusinessDataType) => void
    business: BusinessDataType
    setBusiness: (value: BusinessDataType) => void
    currentTab: string
    setCurrentTab: (value: string) => void
    currentStep: any
    setCurrentStep: (value: any) => void
    mode: ModeType
    setMode: (value: ModeType) => void
    openDetail: {
        open: boolean
        data: any
    }
    setOpenDetail: (value: { open: boolean; data: any }) => void
    openFilter: boolean
    setOpenFilter: (value: boolean) => void
    filters: FiltersType
    setFilters: React.Dispatch<React.SetStateAction<FiltersType>>
    clearFilters: () => void
    applyInitialFilters: () => void
    totalFilters: number
    moveBusiness: (id: number, tab: string, stepID: number, updatedTasks: Task[]) => void
    loadBusiness: () => Promise<void>
    searchIndex: BusinessSearchIndex
    filterByTime: (time: 'today' | 'thisWeek' | 'nextWeek' | 'expired' | null) => void
    handleSearch: (value: string) => void
}

export interface Task extends BusinessSearchDataType {
    id: string
    title: string
    subtitle?: string
    subtitle2?: string
    value?: string
    products?: {
        id: string
        name: string
    }[]
    targets?: FilterType[]
    stepID: string
    dateNotify: string | null
    isExpired: boolean
    professionalID: number | null
}

const Business = createContext<BusinessType | undefined>(undefined)

const emptyFilters: FiltersType = {
    search: '',
    products: [],
    targets: [],
    creators: [],
    companies: [],
    time: null
}

export const BusinessProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
    const { unity, user } = useAuth()
    const { products } = useProduct()
    const currentUserID = user?.userID
    const currentUserName = user?.name
    const [loading, setLoading] = useState(true)
    const [business, setBusiness] = useState<BusinessDataType>({ pipelines: [] })
    const [filteredBusiness, setFilteredBusiness] = useState<BusinessDataType>({ pipelines: [] })
    const [openDetail, setOpenDetail] = useState({ open: false, data: null })
    const [currentTab, setCurrentTab] = useState('tab1')
    const [currentStep, setCurrentStep] = useState(null)
    const [mode, setMode] = useState<ModeType>('pipeline')
    const startLoading = () => setLoading(true)
    const stopLoading = () => setLoading(false)

    //* Filtros (Etiquetas, Criadores, etc.)
    const [openFilter, setOpenFilter] = useState(false)
    const [filters, setFilters] = useState<FiltersType>(emptyFilters)

    const clearFilters = () => {
        setFilters(emptyFilters)
    }

    const applyInitialFilters = useCallback(() => {
        const defaultProduct = products.find(product => product.isDefault)

        setFilters({
            ...emptyFilters,
            creators:
                currentUserID && currentUserName
                    ? [{ id: Number(currentUserID), label: currentUserName }]
                    : [],
            products: defaultProduct
                ? [{ id: Number(defaultProduct.id), label: defaultProduct.name }]
                : []
        })
    }, [currentUserID, currentUserName, products])
    // Conta o total de filtros
    let totalFilters = Object.values(filters).reduce((acc, item) => (Array.isArray(item) ? acc + item.length : acc), 0)
    if (filters.search) totalFilters += 1
    if (filters.time) totalFilters += 1

    const loadBusiness = async () => {
        if (!unity) return

        const response = await fetch(`${api}business/getData`, {
            method: 'POST',
            body: JSON.stringify({
                unityID: unity.unityID
            })
        })
        const result = await response.json()
        setBusiness(result)
    }

    const handleSearch = (value: string) => {
        setFilters(prev => ({ ...prev, search: value }))
    }

    const filterByTime = (time: 'today' | 'thisWeek' | 'nextWeek' | 'expired' | null) => {
        const newTime = filters.time === time ? null : time

        setFilters(prev => ({
            ...prev,
            time: newTime
        }))
    }

    const moveBusiness = async (id: number, tab: string, stepID: number, updatedTasks: Task[]) => {
        if (!id || !tab || !stepID) {
            toast.error('Erro ao mover o negócio. Parâmetros ausentes.')
            return
        }

        try {
            startLoading()

            //* Atualiza DB
            const response = await fetch(`${api}business/moveBusiness`, {
                method: 'POST',
                body: JSON.stringify({
                    businessID: id,
                    stepID
                })
            })

            const result = await response.json()
            if (!response.ok) {
                toast.error(result.message || 'Erro desconhecido ao mover o negócio!')
                return
            }

            if (!result.success) {
                toast.error(result.message || 'Erro ao mover o negócio!')
                return
            }

            //* Atualiza contexto
            const updatedBusiness = filteredBusiness.pipelines.map(pipeline => {
                if (pipeline.value === tab) {
                    return {
                        ...pipeline,
                        steps: pipeline.steps.map(step => {
                            // Limpa as tasks da step atual
                            const updatedStep = {
                                ...step,
                                business: [] // Limpa todas as tasks dessa step
                            }

                            // Insere novamente as tasks que pertencem a essa step
                            const tasksForStep = updatedTasks.filter(task => task.stepID == step.id)

                            return {
                                ...updatedStep,
                                business: [...updatedStep.business, ...tasksForStep] // Insere as tasks da step
                            }
                        })
                    }
                }
                return pipeline
            })

            setFilteredBusiness({ pipelines: updatedBusiness })
            setBusiness({ pipelines: updatedBusiness })

            toast.success(result.message)
        } catch (error) {
            console.log(error)
        } finally {
            stopLoading()
        }
    }

    useEffect(() => {
        //* Carrega pipelines => fases => negócios
        loadBusiness()
    }, [unity])

    //* Índice de busca pré-computado (recalcula somente quando os dados mudam)
    const searchIndex = useMemo(() => buildBusinessSearchIndex(business), [business])

    useEffect(() => {
        setFilteredBusiness(businessFilter(business, filters, searchIndex))
    }, [business, filters, searchIndex])

    return (
        <Business.Provider
            value={{
                loading,
                startLoading,
                stopLoading,
                business,
                setBusiness,
                filteredBusiness,
                setFilteredBusiness,
                openDetail,
                setOpenDetail,
                mode,
                setMode,
                currentTab,
                setCurrentTab,
                currentStep,
                setCurrentStep,
                openFilter,
                setOpenFilter,
                filters,
                setFilters,
                clearFilters,
                applyInitialFilters,
                totalFilters,
                moveBusiness,
                loadBusiness,
                searchIndex,
                filterByTime,
                handleSearch
            }}
        >
            {children}
        </Business.Provider>
    )
}

export function useBusiness() {
    const context = useContext(Business)
    if (context === undefined) {
        throw new Error('useCreate must be used within a BusinessProvider')
    }
    return context
}
